// ============================================================
// Airbnb income — what a holiday-let apartment earned, per month.
//
// DreamHaven runs these apartments and reports, per unit and calendar month of
// the stay: the host payout (already after the Airbnb / Booking.com cut),
// cleaning, the Tourism Dirham, their 20% fee, the net to the owner and the
// occupancy. The backend (Backend/airbnbIncomeRoutes.js) syncs that daily and
// returns only the signed-in owner's own units.
//
// Two places use it:
//   • AirbnbPortfolioSection — on the Property Management overview, one line
//     per Airbnb apartment. Renders nothing for an owner without one.
//   • AirbnbUnitTab — the "Airbnb" sub-tab of a unit, month by month.
//
// Nothing here estimates. A month DreamHaven has not reported is not shown,
// and the open month is labelled provisional because it still moves.
// ============================================================
import React, { useEffect, useState } from 'react';
import { ChevronDown, ChevronRight, BedDouble, ExternalLink } from 'lucide-react';
import { M, T } from '../shared/pmTokens';
import { portalFetch } from '../shared/api';
import { useLanguage } from '../../components/LanguageContext';
import { isFilmMode } from '../demo/demoMode';
import { filmAirbnbPayload, filmAirbnbBookings } from '../demo/demoAirbnbData';

export interface AirbnbMonth {
  month: string;              // YYYY-MM, calendar month of the stay
  gross: number;              // host payout after the platform commission
  cleaning: number;
  tourismDirham: number;
  managementFee: number;
  net: number;                // to the owner
  bookedNights: number;
  availableNights: number;
  bookings?: number | null;
  channels?: any;
  currency: string;
  ownerPayoutDate?: string | null;
  status: 'final' | 'provisional';
  revised?: boolean;
}

export interface AirbnbUnit {
  unitId: string;
  name: string;
  apartment: string;
  manager: string;
  /** The public Airbnb listing, set when the unit is linked. */
  listingUrl?: string | null;
  months: AirbnbMonth[];      // newest first
}

interface AirbnbPayload { units: AirbnbUnit[]; lastSyncAt: string | null; sample?: boolean }

// ---------------- data ----------------

// One request serves the overview and every unit page opened after it.
let cache: { at: number; promise: Promise<AirbnbPayload> } | null = null;
const CACHE_MS = 60 * 1000;

/**
 * Local preview only: `?airbnb=sample` in a development build puts DreamHaven's
 * own worked example (5 nights, AED 5,000 payout, 2026-09) on the first two
 * units, labelled as sample. A production build never takes this path.
 */
function samplePayload(unitIds: string[]): AirbnbPayload | null {
  if (process.env.NODE_ENV === 'production') return null;
  try {
    if (new URLSearchParams(window.location.search).get('airbnb') !== 'sample') return null;
  } catch { return null; }
  const row: AirbnbMonth = {
    month: '2026-09', gross: 5000, cleaning: 320, tourismDirham: 50, managementFee: 926, net: 3704,
    bookedNights: 5, availableNights: 30, currency: 'AED', ownerPayoutDate: null, status: 'final',
  };
  const open: AirbnbMonth = { ...row, month: '2026-10', bookedNights: 5, availableNights: 31, status: 'provisional' };
  const names = [
    ['Residence Diana', 'B1-606', 'https://www.airbnb.com/rooms/1757902316076838790'],
    ['Residence Aurelia', 'B3-802', 'https://www.airbnb.com/rooms/1711714375198303735'],
  ];
  return {
    sample: true,
    lastSyncAt: null,
    units: unitIds.slice(0, 2).map((unitId, i) => ({
      unitId, name: names[i][0], apartment: names[i][1], manager: 'DreamHaven', listingUrl: names[i][2], months: [open, row],
    })),
  };
}

function loadAirbnb(): Promise<AirbnbPayload> {
  if (cache && Date.now() - cache.at < CACHE_MS) return cache.promise;
  const promise = portalFetch<AirbnbPayload>('/airbnb-income').catch(() => ({ units: [], lastSyncAt: null }));
  cache = { at: Date.now(), promise };
  return promise;
}

/**
 * The owner's Airbnb units. Empty — never an error — when the feature is off
 * (the route 404s), the owner has none, or the network fails: in every one of
 * those cases the right screen is the one without an Airbnb section.
 */
export function useAirbnbIncome(unitIds: string[], demoMode?: boolean): AirbnbPayload {
  const [data, setData] = useState<AirbnbPayload>({ units: [], lastSyncAt: null });
  const idsKey = unitIds.join('|');
  useEffect(() => {
    // Video-demo channel (film mode): the live Airbnb section on invented
    // figures, so films match the live app. Hostname-gated — never live.
    if (demoMode && isFilmMode()) { setData(filmAirbnbPayload(unitIds)); return; }
    // The code-gated showcase keeps its own sample portfolio untouched.
    if (demoMode) return;
    const sample = samplePayload(unitIds);
    if (sample) { setData(sample); return; }
    let alive = true;
    loadAirbnb().then((d) => { if (alive) setData(d || { units: [], lastSyncAt: null }); });
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [idsKey, demoMode]);
  return data;
}

// ---------------- figures ----------------

const aed = (n: number) => Math.round(n).toLocaleString('en-AE');
const pct = (booked: number, avail: number) => (avail > 0 ? Math.round((booked / avail) * 100) : null);

/**
 * The months that belong to the apartment's letting history, newest first.
 *
 * The feed reports a full year for every unit, including the months before
 * the listing went live (0 of 31 nights). Those are not vacancy: counted, they
 * pulled Diana's occupancy to 10% on 13 months when it had only been let for
 * two. History starts at the first month with a booked night; a quiet month
 * AFTER that is real vacancy and stays in.
 */
export function activeMonths(months: AirbnbMonth[]): AirbnbMonth[] {
  const asc = [...months].sort((a, b) => (a.month < b.month ? -1 : 1));
  const first = asc.findIndex((m) => m.bookedNights > 0);
  return first < 0 ? [] : asc.slice(first).reverse();
}

function totals(months: AirbnbMonth[]) {
  // Last 12 CLOSED months of letting: the open month is shown on its own as "so far".
  const closed = activeMonths(months).filter((m) => m.status === 'final').slice(0, 12);
  const net = closed.reduce((s, m) => s + m.net, 0);
  const booked = closed.reduce((s, m) => s + m.bookedNights, 0);
  const avail = closed.reduce((s, m) => s + m.availableNights, 0);
  const open = months.find((m) => m.status === 'provisional') || null;
  const since = closed.length ? closed[closed.length - 1].month : null;
  return { net, booked, occupancy: pct(booked, avail), open, closedCount: closed.length, since };
}

/** "Net to you, last 12 months" — or "since <month>" while the history is shorter. */
function useNetLabel() {
  const { t, language } = useLanguage();
  return (closedCount: number, since: string | null) =>
    closedCount >= 12 || !since ? t('pmAirbnbNet12') : `${t('pmAirbnbNetSince')} ${monthLabel(since, language)}`;
}

/** Portfolio months: every unit's letting months summed per calendar month. */
function sumByMonth(units: AirbnbUnit[]): AirbnbMonth[] {
  const by = new Map<string, AirbnbMonth>();
  for (const u of units) {
    for (const m of activeMonths(u.months)) {
      const p = by.get(m.month);
      by.set(m.month, p ? {
        ...p,
        gross: p.gross + m.gross, cleaning: p.cleaning + m.cleaning, tourismDirham: p.tourismDirham + m.tourismDirham,
        managementFee: p.managementFee + m.managementFee, net: p.net + m.net,
        bookedNights: p.bookedNights + m.bookedNights, availableNights: p.availableNights + m.availableNights,
        bookings: (p.bookings || 0) + (m.bookings || 0),
        status: p.status === 'provisional' || m.status === 'provisional' ? 'provisional' : 'final',
      } : { ...m });
    }
  }
  return [...by.values()].sort((a, b) => (a.month < b.month ? 1 : -1));
}

function monthLabel(month: string, lang: string) {
  const [y, m] = month.split('-').map(Number);
  return new Date(Date.UTC(y, m - 1, 1)).toLocaleDateString(lang === 'de' ? 'de-DE' : 'en-GB', { month: 'long', year: 'numeric', timeZone: 'UTC' });
}

// Same money face as the rest of the management statement.
const figure: React.CSSProperties = {
  fontFamily: "'Cormorant Garamond', 'EB Garamond', Georgia, serif",
  fontWeight: 600,
  color: M.black,
  letterSpacing: '-0.005em',
  fontVariantNumeric: 'lining-nums proportional-nums',
};

const card: React.CSSProperties = { background: '#fff', borderRadius: 10, border: `1px solid ${M.hair}` };

const Chip: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <span style={{ fontSize: T.micro - 1, fontWeight: 700, color: M.goldInk, background: M.tint, border: `1px solid ${M.tintEdge}`, borderRadius: 99, padding: '2px 9px', whiteSpace: 'nowrap' }}>
    {children}
  </span>
);

const Stat: React.FC<{ label: string; value: string }> = ({ label, value }) => (
  <div style={{ minWidth: 0 }}>
    <div style={{ fontSize: T.micro, color: M.ink3, fontWeight: 600 }}>{label}</div>
    <div style={{ fontSize: T.statValue, fontWeight: 800, color: M.black, marginTop: 2 }}>{value}</div>
  </div>
);

// ---------------- bar chart ----------------

// Darker than the brand gold on purpose: #D6B160 on white is under 2:1 and
// fails the lightness band for a bar fill. #A0791C is the same gold family and
// passes the dataviz validator (lightness, chroma, >= 3:1 on white).
const BAR = '#A0791C';

/**
 * Net to the owner per month, one bar each, oldest left.
 *
 * Touch first: tapping a bar puts its month in the readout line above the
 * plot (there is no hover on a phone). The open month is drawn at reduced
 * strength and read out as "so far", because it still moves. The month list
 * below the chart is the table view of the same figures.
 */
export const MonthBars: React.FC<{ months: AirbnbMonth[] }> = ({ months }) => {
  const { t, language } = useLanguage();
  const shown = [...months].sort((a, b) => (a.month < b.month ? -1 : 1)).slice(-13);
  const latestClosed = [...shown].reverse().find((m) => m.status === 'final') || shown[shown.length - 1];
  const [sel, setSel] = useState<string | null>(null);
  if (!shown.length) return null;
  const pick = shown.find((m) => m.month === sel) || latestClosed;
  const max = Math.max(1, ...shown.map((m) => m.net));
  const compact = shown.length > 6;
  const kShort = (n: number) => (Math.abs(n) >= 1000 ? `${(n / 1000).toFixed(1).replace(/\.0$/, '')}k` : String(Math.round(n)));
  const occ = pct(pick.bookedNights, pick.availableNights);
  const short = (month: string) => {
    const [y, mm] = month.split('-').map(Number);
    return new Date(Date.UTC(y, mm - 1, 1)).toLocaleDateString(language === 'de' ? 'de-DE' : 'en-GB', { month: 'short', timeZone: 'UTC' }).replace('.', '');
  };

  return (
    <div>
      <div style={{ fontSize: T.cardTitle, fontWeight: 800, color: M.black }}>{t('pmAirbnbChartTitle')}</div>
      {/* Readout — the tapped month, or the latest closed one. */}
      <div aria-live="polite" style={{ display: 'flex', alignItems: 'baseline', gap: 8, flexWrap: 'wrap', marginTop: 6, minHeight: 28 }}>
        <span style={{ fontSize: T.secondary, fontWeight: 700, color: M.ink2 }}>
          {monthLabel(pick.month, language)}{pick.status === 'provisional' ? ` · ${t('pmAirbnbSoFar')}` : ''}
        </span>
        <span style={{ ...figure, fontSize: T.statValue + 2 }}>AED {aed(pick.net)}</span>
        <span style={{ fontSize: T.micro, color: M.ink3 }}>
          {pick.bookedNights}/{pick.availableNights} {t('pmAirbnbNights')}{occ !== null ? ` · ${occ}%` : ''}
        </span>
      </div>
      <div style={{ position: 'relative', height: 150, marginTop: 10 }}>
        <div style={{ position: 'absolute', left: 0, right: 0, bottom: 0, borderTop: `1px solid ${M.ink4}` }} />
        <div role="list" style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'flex-end', gap: 2 }}>
          {shown.map((m) => {
            const on = m.month === pick.month;
            // 22px of headroom above the tallest bar for its amount.
            const h = m.net > 0 ? Math.max(3, (m.net / max) * 128) : 0;
            return (
              <button
                key={m.month}
                role="listitem"
                aria-label={`${monthLabel(m.month, language)}: AED ${aed(m.net)}`}
                aria-pressed={on}
                onClick={() => setSel(m.month)}
                style={{ flex: 1, minWidth: 0, height: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'flex-end', background: on ? M.wash : 'transparent', border: 'none', borderRadius: 4, padding: 0, cursor: 'pointer' }}
              >
                {/* The amount on every bar, in text ink (never the bar colour).
                    Full figures while they fit; past six bars a phone column
                    is ~25px wide, so they shorten to 4.3k. */}
                <span style={{ fontSize: 12, lineHeight: '16px', marginBottom: 3, color: on ? M.black : M.ink2, fontWeight: on ? 800 : 600, whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums' }}>
                  {compact ? kShort(m.net) : aed(m.net)}
                </span>
                <span style={{
                  display: 'block', width: '62%', maxWidth: 28, height: h, borderRadius: '4px 4px 0 0',
                  background: BAR, opacity: m.status === 'provisional' ? 0.42 : on ? 1 : 0.85,
                }} />
              </button>
            );
          })}
        </div>
      </div>
      <div style={{ display: 'flex', gap: 2, marginTop: 6 }}>
        {shown.map((m) => (
          <span key={m.month} style={{ flex: 1, minWidth: 0, textAlign: 'center', fontSize: 12, color: m.month === pick.month ? M.black : M.ink3, fontWeight: m.month === pick.month ? 800 : 500, overflow: 'hidden', whiteSpace: 'nowrap' }}>
            {short(m.month)}
          </span>
        ))}
      </div>
    </div>
  );
};

// ---------------- overview section ----------------

/** One line per Airbnb apartment on the management overview. */
export const AirbnbPortfolioSection: React.FC<{
  units: any[];
  demoMode?: boolean;
  onOpenUnit: (unitId: string) => void;
}> = ({ units, demoMode, onOpenUnit }) => {
  const { t } = useLanguage();
  const netLabel = useNetLabel();
  const data = useAirbnbIncome((units || []).map((u) => String(u.id)), demoMode);
  if (!data.units.length) return null;

  const all = data.units.map((u) => ({ u, s: totals(u.months) }));
  const net = all.reduce((a, x) => a + x.s.net, 0);
  // The portfolio history runs from the earliest apartment's first booking.
  const sinces = all.map((x) => x.s.since).filter(Boolean) as string[];
  const since = sinces.length ? sinces.sort()[0] : null;
  const longest = Math.max(0, ...all.map((x) => x.s.closedCount));

  return (
    <div style={{ padding: '0 16px' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '26px 0 10px' }}>
        <span style={{ fontSize: T.sectionTitle, fontWeight: 800, color: M.black, whiteSpace: 'nowrap' }}>{t('pmAirbnbIncome')}</span>
        {data.sample && <Chip>{t('pmAirbnbSample')}</Chip>}
        <span style={{ flex: 1, height: 1, background: M.hair }} />
      </div>
      <div style={{ ...card, padding: '16px 16px 6px' }}>
        <div style={{ fontSize: T.micro, color: M.ink3, fontWeight: 600 }}>{netLabel(longest, since)}</div>
        <div style={{ ...figure, fontSize: T.money, lineHeight: 1.15 }}>
          <span style={{ fontSize: T.money - 14, color: M.gold, marginRight: 6 }}>AED</span>{aed(net)}
        </div>
        <div style={{ fontSize: T.micro, color: M.ink3, marginTop: 2 }}>{t('pmAirbnbManagedBy')}</div>
        <div style={{ marginTop: 10 }}>
          {all.map(({ u, s }) => (
            <button
              key={u.unitId}
              onClick={() => onOpenUnit(u.unitId)}
              className="pt-tap"
              style={{ width: '100%', display: 'flex', alignItems: 'center', gap: 12, padding: '14px 0', background: 'none', border: 'none', borderTop: `1px solid ${M.hair}`, cursor: 'pointer', textAlign: 'left' }}
            >
              <BedDouble size={20} color={M.goldInk} style={{ flexShrink: 0 }} />
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: T.body, fontWeight: 800, color: M.black, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {[u.apartment, u.name].filter(Boolean).join(' · ')}
                </div>
                <div style={{ fontSize: T.micro, color: M.ink3, marginTop: 2 }}>
                  {s.occupancy !== null ? `${t('pmAirbnbOccupancy')} ${s.occupancy}%` : '—'}
                  {s.open ? ` · ${t('pmAirbnbThisMonth')} AED ${aed(s.open.net)}` : ''}
                </div>
              </div>
              <div style={{ ...figure, fontSize: T.statValue + 2, whiteSpace: 'nowrap' }}>AED {aed(s.net)}</div>
              <ChevronRight size={18} color={M.ink4} style={{ flexShrink: 0 }} />
            </button>
          ))}
        </div>
      </div>
      <div style={{ ...card, padding: 16, marginTop: 14 }}>
        <MonthBars months={sumByMonth(data.units)} />
      </div>
    </div>
  );
};

// ---------------- unit sub-tab ----------------

const Line: React.FC<{ label: string; value: string; strong?: boolean; minus?: boolean }> = ({ label, value, strong, minus }) => (
  <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, padding: '6px 0', fontSize: T.secondary, color: strong ? M.black : M.ink2, fontWeight: strong ? 800 : 500, borderTop: strong ? `1px solid ${M.hair}` : 'none', marginTop: strong ? 4 : 0 }}>
    <span>{label}</span>
    <span style={{ whiteSpace: 'nowrap' }}>{minus ? '− ' : ''}AED {value}</span>
  </div>
);

const MonthRow: React.FC<{ m: AirbnbMonth; open: boolean; onToggle: () => void }> = ({ m, open, onToggle }) => {
  const { t, language } = useLanguage();
  const occ = pct(m.bookedNights, m.availableNights);
  return (
    <div style={{ borderTop: `1px solid ${M.hair}` }}>
      <button
        onClick={onToggle}
        aria-expanded={open}
        className="pt-tap"
        style={{ width: '100%', display: 'flex', alignItems: 'center', gap: 10, padding: '14px 0', background: 'none', border: 'none', cursor: 'pointer', textAlign: 'left' }}
      >
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
            <span style={{ fontSize: T.body, fontWeight: 800, color: M.black }}>{monthLabel(m.month, language)}</span>
            {m.status === 'provisional' && <Chip>{t('pmAirbnbProvisional')}</Chip>}
            {m.revised && <Chip>{t('pmAirbnbCorrected')}</Chip>}
          </div>
          <div style={{ fontSize: T.micro, color: M.ink3, marginTop: 2 }}>
            {m.bookedNights}/{m.availableNights} {t('pmAirbnbNights')}{occ !== null ? ` · ${occ}%` : ''}
            {typeof m.bookings === 'number' ? ` · ${m.bookings} ${t('pmAirbnbBookings')}` : ''}
          </div>
        </div>
        <div style={{ ...figure, fontSize: T.statValue + 2, whiteSpace: 'nowrap' }}>AED {aed(m.net)}</div>
        <ChevronDown size={18} color={M.ink4} style={{ flexShrink: 0, transform: open ? 'rotate(180deg)' : 'none', transition: 'transform .15s' }} />
      </button>
      {open && (
        <div style={{ background: M.wash, borderRadius: 8, padding: '8px 12px', marginBottom: 12 }}>
          <Line label={t('pmAirbnbGross')} value={aed(m.gross)} />
          <Line label={t('pmAirbnbCleaning')} value={aed(m.cleaning)} minus />
          <Line label={t('pmAirbnbTourism')} value={aed(m.tourismDirham)} minus />
          <Line label={t('pmAirbnbFee')} value={aed(m.managementFee)} minus />
          <Line label={t('pmAirbnbNet')} value={aed(m.net)} strong />
        </div>
      )}
    </div>
  );
};

/** The "Airbnb" sub-tab of one unit. */
export const AirbnbUnitTab: React.FC<{ unit: AirbnbUnit; sample?: boolean; lastSyncAt?: string | null }> = ({ unit, sample, lastSyncAt }) => {
  const { t, language } = useLanguage();
  const netLabel = useNetLabel();
  // Only months with a booking are listed; the rest carry nothing to read.
  const booked = unit.months.filter((m) => m.bookedNights > 0);
  const [openMonth, setOpenMonth] = useState<string | null>(booked[0]?.month || null);
  const s = totals(unit.months);

  return (
    <div style={{ padding: '16px 16px 4px' }}>
      <div style={{ ...card, padding: 16 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
          <span style={{ fontSize: T.micro, color: M.ink3, fontWeight: 600 }}>{netLabel(s.closedCount, s.since)}</span>
          {sample && <Chip>{t('pmAirbnbSample')}</Chip>}
        </div>
        <div style={{ ...figure, fontSize: T.money, lineHeight: 1.15 }}>
          <span style={{ fontSize: T.money - 14, color: M.gold, marginRight: 6 }}>AED</span>{aed(s.net)}
        </div>
        <div style={{ fontSize: T.micro, color: M.ink3, marginTop: 2 }}>{t('pmAirbnbManagedBy')}</div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, minmax(0, 1fr))', gap: 12, marginTop: 14 }}>
          <Stat label={t('pmAirbnbOccupancy')} value={s.occupancy !== null ? `${s.occupancy}%` : '—'} />
          <Stat label={t('pmAirbnbNightsBooked')} value={String(s.booked)} />
          <Stat label={t('pmAirbnbThisMonth')} value={s.open ? `AED ${aed(s.open.net)}` : '—'} />
        </div>
        {unit.listingUrl && (
          <a
            href={unit.listingUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="pt-tap"
            style={{ display: 'inline-flex', alignItems: 'center', gap: 7, marginTop: 16, padding: '9px 16px', borderRadius: 99, border: `1px solid ${M.tintEdge}`, background: M.tint, color: M.goldInk, fontSize: T.secondary, fontWeight: 800, textDecoration: 'none' }}
          >
            <ExternalLink size={16} /> {t('pmAirbnbViewListing')}
          </a>
        )}
      </div>

      {booked.length > 0 && (
        <div style={{ ...card, padding: 16, marginTop: 14 }}>
          <MonthBars months={activeMonths(unit.months)} />
        </div>
      )}

      <div style={{ ...card, padding: '4px 16px', marginTop: 14 }}>
        <div style={{ fontSize: T.cardTitle, fontWeight: 800, color: M.black, padding: '12px 0 10px' }}>{t('pmAirbnbMonths')}</div>
        {booked.length === 0 ? (
          <p style={{ fontSize: T.secondary, color: M.ink3, padding: '4px 0 16px', margin: 0 }}>{t('pmAirbnbEmpty')}</p>
        ) : (
          booked.map((m) => (
            <MonthRow key={m.month} m={m} open={openMonth === m.month} onToggle={() => setOpenMonth(openMonth === m.month ? null : m.month)} />
          ))
        )}
      </div>

      <BookingsCard unitId={unit.unitId} sample={sample} />

      <p style={{ fontSize: T.micro, color: M.ink3, lineHeight: 1.5, margin: '12px 2px 0' }}>
        {t('pmAirbnbHowNet')} {s.open ? t('pmAirbnbProvisionalNote') : ''}
        {lastSyncAt ? ` ${t('pmAirbnbUpdated')}: ${new Date(lastSyncAt).toLocaleString(language === 'de' ? 'de-DE' : 'en-GB', { dateStyle: 'medium', timeStyle: 'short' })}.` : ''}
      </p>
    </div>
  );
};

// ---------------- bookings ----------------

interface AirbnbBooking { bookingId: string; checkIn: string | null; checkOut: string | null; nights: number | null; guests: number | null; channel: string | null; payoutAed: number }

const CHANNEL_LABEL: Record<string, string> = { airbnb: 'Airbnb', booking_com: 'Booking.com', direct: 'Direct' };

/**
 * Each confirmed stay of one apartment (GET /api/airbnb-income/bookings →
 * DreamHaven owner-bookings). No guest names exist in the feed. The amount is
 * the platform payout for the whole stay — gross, before cleaning, Tourism
 * Dirham and the fee — so it is labelled as such and never called "net".
 */
const BookingsCard: React.FC<{ unitId: string; sample?: boolean }> = ({ unitId, sample }) => {
  const { t, language } = useLanguage();
  const de = language === 'de';
  const [rows, setRows] = useState<AirbnbBooking[] | null>(null);
  const [failed, setFailed] = useState(false);
  useEffect(() => {
    if (sample) { setRows([]); return; }
    if (isFilmMode()) { setRows(filmAirbnbBookings(unitId)); return; }
    let alive = true;
    portalFetch<{ bookings: AirbnbBooking[] }>(`/airbnb-income/bookings?unitId=${encodeURIComponent(unitId)}`)
      .then((d) => { if (alive) setRows(d.bookings || []); })
      .catch(() => { if (alive) { setFailed(true); setRows([]); } });
    return () => { alive = false; };
  }, [unitId, sample]);
  if (sample) return null;
  const date = (s: string | null) => {
    const d = s ? new Date(`${s}T00:00:00`) : null;
    return d && !isNaN(d.getTime()) ? d.toLocaleDateString(de ? 'de-DE' : 'en-GB', { day: 'numeric', month: 'short' }) : '—';
  };
  const year = (s: string | null) => (s ? s.slice(0, 4) : '');
  return (
    <div style={{ ...card, padding: '4px 16px', marginTop: 14 }}>
      <div style={{ fontSize: T.cardTitle, fontWeight: 800, color: M.black, padding: '12px 0 10px' }}>
        {t('pmAirbnbBookings')}{rows && rows.length > 0 ? ` · ${rows.length}` : ''}
      </div>
      {rows === null ? (
        <p style={{ fontSize: T.secondary, color: M.ink3, padding: '4px 0 16px', margin: 0 }}>…</p>
      ) : rows.length === 0 ? (
        <p style={{ fontSize: T.secondary, color: M.ink3, padding: '4px 0 16px', margin: 0 }}>{failed ? t('pmAirbnbBookingsError') : t('pmAirbnbBookingsEmpty')}</p>
      ) : (
        rows.map((b) => (
          <div key={b.bookingId || `${b.checkIn}-${b.checkOut}`} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '11px 0', borderTop: `1px solid ${M.hair}` }}>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: T.body, fontWeight: 700, color: M.black }}>
                {date(b.checkIn)} – {date(b.checkOut)} {year(b.checkOut)}
              </div>
              <div style={{ fontSize: T.micro, color: M.ink3, marginTop: 2 }}>
                {b.nights != null ? `${b.nights} ${b.nights === 1 ? t('pmAirbnbNight') : t('pmAirbnbNights')}` : ''}
                {b.guests != null ? ` · ${b.guests} ${b.guests === 1 ? t('pmAirbnbGuest') : t('pmAirbnbGuests')}` : ''}
                {b.channel ? ` · ${CHANNEL_LABEL[b.channel] || b.channel}` : ''}
              </div>
            </div>
            <div style={{ ...figure, fontSize: T.body, whiteSpace: 'nowrap' }}>AED {aed(b.payoutAed)}</div>
          </div>
        ))
      )}
      {rows && rows.length > 0 && (
        <p style={{ fontSize: T.micro, color: M.ink3, lineHeight: 1.5, margin: 0, padding: '10px 0 14px', borderTop: `1px solid ${M.hair}` }}>{t('pmAirbnbBookingsNote')}</p>
      )}
    </div>
  );
};

// ---------------- Portfolio report ----------------

/**
 * "Airbnb income" in the Portfolio report, after Revenue & return. Same
 * figures as the Property Management Airbnb tab: per apartment, net to the
 * owner over the letting history (max. 12 closed months), occupancy and
 * nights, and the monthly bars. Renders nothing without an Airbnb apartment.
 */
export const AirbnbReportSection: React.FC<{ units: any[]; demoMode?: boolean }> = ({ units, demoMode }) => {
  const { t } = useLanguage();
  const netLabel = useNetLabel();
  const data = useAirbnbIncome((units || []).map((u) => String(u.id ?? u.unitId ?? '')), demoMode);
  if (!data.units.length) return null;
  const all = data.units.map((u) => ({ u, s: totals(u.months) }));
  const net = all.reduce((a, x) => a + x.s.net, 0);
  const booked = all.reduce((a, x) => a + x.s.booked, 0);
  const sinces = all.map((x) => x.s.since).filter(Boolean) as string[];
  const since = sinces.length ? sinces.sort()[0] : null;
  const longest = Math.max(0, ...all.map((x) => x.s.closedCount));
  const cell: React.CSSProperties = { padding: '10px 8px', fontSize: T.secondary, borderTop: `1px solid ${M.hair}` };
  const head: React.CSSProperties = { ...cell, fontSize: T.micro, color: M.ink3, fontWeight: 600, borderTop: 'none' };

  return (
    <div className="pr-root pr-embedded" style={{ marginTop: 20 }}>
      <div className="pr-sec-head">
        <h2 className="pr-sec-title pr-serif">{t('pmAirbnbIncome')}</h2>
        <span className="pr-sec-line" />
      </div>
      <div style={{ ...card, padding: 16 }}>
        <div style={{ fontSize: T.micro, color: M.ink3, fontWeight: 600 }}>{netLabel(longest, since)}</div>
        <div style={{ ...figure, fontSize: T.money, lineHeight: 1.15 }}>
          <span style={{ fontSize: T.money - 14, color: M.gold, marginRight: 6 }}>AED</span>{aed(net)}
        </div>
        <div style={{ fontSize: T.micro, color: M.ink3, marginTop: 2 }}>{t('pmAirbnbManagedBy')}</div>
        <div style={{ overflowX: 'auto', marginTop: 14 }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 420 }}>
            <thead>
              <tr>
                <th style={{ ...head, textAlign: 'left' }}>{t('pmAirbnbApartment')}</th>
                <th style={{ ...head, textAlign: 'right' }}>{t('pmAirbnbOccupancy')}</th>
                <th style={{ ...head, textAlign: 'right' }}>{t('pmAirbnbNightsBooked')}</th>
                <th style={{ ...head, textAlign: 'right' }}>{t('pmAirbnbNet')}</th>
              </tr>
            </thead>
            <tbody>
              {all.map(({ u, s }) => (
                <tr key={u.unitId}>
                  <td style={{ ...cell, fontWeight: 700 }}>{[u.apartment, u.name].filter(Boolean).join(' · ')}</td>
                  <td style={{ ...cell, textAlign: 'right' }}>{s.occupancy !== null ? `${s.occupancy}%` : '—'}</td>
                  <td style={{ ...cell, textAlign: 'right' }}>{s.booked}</td>
                  <td style={{ ...cell, textAlign: 'right', fontWeight: 700 }}>AED {aed(s.net)}</td>
                </tr>
              ))}
              {all.length > 1 && (
                <tr>
                  <td style={{ ...cell, fontWeight: 800 }}>{t('pmAirbnbTotal')}</td>
                  <td style={cell} />
                  <td style={{ ...cell, textAlign: 'right', fontWeight: 800 }}>{booked}</td>
                  <td style={{ ...cell, textAlign: 'right', fontWeight: 800 }}>AED {aed(net)}</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
      <div style={{ ...card, padding: 16, marginTop: 14 }}>
        <MonthBars months={sumByMonth(data.units)} />
      </div>
      <p style={{ fontSize: T.micro, color: M.ink3, lineHeight: 1.5, margin: '10px 2px 0' }}>{t('pmAirbnbHowNet')}</p>
    </div>
  );
};
